/*
 * Licensed to Elasticsearch B.V. under one or more contributor
 * license agreements. See the NOTICE file distributed with
 * this work for additional information regarding copyright
 * ownership. Elasticsearch B.V. licenses this file to you under
 * the Apache License, Version 2.0 (the "License"); you may
 * not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *    http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing,
 * software distributed under the License is distributed on an
 * "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
 * KIND, either express or implied.  See the License for the
 * specific language governing permissions and limitations
 * under the License.
 */
import React from 'react';
import PropTypes from "prop-types";
import classNames from 'classnames';
import { EuiBeacon } from '../beacon';
import { EuiButtonEmpty } from '../button';
import { EuiFlexGroup, EuiFlexItem } from '../flex';
import { EuiI18n } from '../i18n';
import { EuiPopover, EuiPopoverFooter, EuiPopoverTitle } from '../popover';
import { EuiTitle } from '../title';
import { EuiTourStepIndicator } from './tour_step_indicator';
export var EuiTourStep = ({
  anchorPosition = 'leftUp',
  children,
  className,
  closePopover = () => {},
  content,
  isStepOpen = false,
  minWidth = true,
  onFinish,
  step = 1,
  stepsTotal,
  style,
  subtitle,
  title,
  decoration = 'beacon',
  footerAction,
  ...rest
}) => {
  if (step === 0) {
    console.warn('EuiTourStep `step` should 1-based indexing. Please update to eliminate 0 indexes.');
  }

  let newStyle;
  let widthClassName;

  if (minWidth === true) {
    widthClassName = 'euiTour--minWidth-default';
  } else if (minWidth !== false) {
    const value = typeof minWidth === 'number' ? `${minWidth}px` : minWidth;
    newStyle = { ...style,
      minWidth: value
    };
  }

  const classes = classNames('euiTour', widthClassName, className);
  const finishButtonProps = {
    color: 'text',
    flush: 'right',
    size: 'xs'
  };
  const footer = <EuiFlexGroup responsive={false} justifyContent={stepsTotal > 1 ? 'spaceBetween' : 'flexEnd'}>
      {stepsTotal > 1 && <EuiFlexItem grow={false}>
          <ul className="euiTourFooter__stepList">
            {[...Array(stepsTotal).keys()].map((_, i) => {
          let status = 'complete';

          if (step === i + 1) {
            status = 'active';
          } else if (step <= i) {
            status = 'incomplete';
          }

          return <EuiTourStepIndicator key={i} number={i + 1} status={status} />;
        })}
          </ul>
        </EuiFlexItem>}

      {footerAction ? <EuiFlexItem grow={false}>{footerAction}</EuiFlexItem> : <EuiFlexItem grow={false}>
          <EuiI18n tokens={['euiTourStep.endTour', 'euiTourStep.skipTour', 'euiTourStep.closeTour']} defaults={['End tour', 'Skip tour', 'Close']}>
            {([endTour, skipTour, closeTour]) => {
          let content = closeTour;

          if (stepsTotal > 1) {
            content = stepsTotal === step ? endTour : skipTour;
          }

          return <EuiButtonEmpty onClick={onFinish} {...finishButtonProps}>
                  {content}
                </EuiButtonEmpty>;
        }}
          </EuiI18n>
        </EuiFlexItem>}
    </EuiFlexGroup>;
  const hasBeacon = decoration === 'beacon';
  return <EuiPopover anchorPosition={anchorPosition} button={children} closePopover={closePopover} isOpen={isStepOpen} panelClassName={classes} panelStyle={newStyle || style} offset={hasBeacon ? 10 : 0} arrowChildren={hasBeacon && <EuiBeacon className="euiTour__beacon" />} withTitle {...rest}>
      <EuiPopoverTitle className="euiTourHeader">
        <EuiTitle size="xxxs" className="euiTourHeader__subtitle">
          <h1>{subtitle}</h1>
        </EuiTitle>
        <EuiTitle size="xxs" className="euiTourHeader__title">
          <h2>{title}</h2>
        </EuiTitle>
      </EuiPopoverTitle>
      <div className="euiTour__content">{content}</div>
      <EuiPopoverFooter className="euiTourFooter">{footer}</EuiPopoverFooter>
    </EuiPopover>;
};
EuiTourStep.propTypes = {
  /**
     * Element to which the tour step popover attaches when open
     */
  children: PropTypes.element.isRequired,

  /**
     * Contents of the tour step popover
     */
  content: PropTypes.node.isRequired,

  /**
     * Step will display if set to `true`
     */
  isStepOpen: PropTypes.bool,

  /**
     * Sets the min-width of the tour popover,
     * set to `true` to use the default size,
     * set to `false` to not restrict the width,
     * set to a number for a custom width in px,
     * set to a string for a custom width in custom measurement.
     */
  minWidth: PropTypes.oneOfType([PropTypes.bool.isRequired, PropTypes.number.isRequired, PropTypes.string.isRequired]),

  /**
     * Function to call for 'Skip tour' and 'End tour' actions
     */
  onFinish: PropTypes.func.isRequired,

  /**
     * The number of the step within the parent tour. 1-based indexing.
     */
  step: PropTypes.number.isRequired,

  /**
     * The total number of steps in the tour
     */
  stepsTotal: PropTypes.number.isRequired,

  /**
     * Optional, standard DOM `style` attribute. Passed to the EuiPopover panel.
     */
  style: PropTypes.any,

  /**
     * Smaller title text that appears atop each step in the tour
     */
  subtitle: PropTypes.string.isRequired,

  /**
     * Larger title text specific to this step
     */
  title: PropTypes.string.isRequired,

  /**
     * Extra visual indication of step location
     */
  decoration: PropTypes.oneOf(["none", "beacon"]),

  /**
     * Element to replace the 'Skip tour' link in the footer
     */
  footerAction: PropTypes.element,
  className: PropTypes.string,
  "aria-label": PropTypes.string,
  "data-test-subj": PropTypes.string
};

try {
  // @ts-ignore
  EuiTourStep.displayName = "EuiTourStep"; // @ts-ignore

  EuiTourStep.__docgenInfo = {
    "description": "",
    "displayName": "EuiTourStep",
    "props": {
      "children": {
        "defaultValue": null,
        "description": "Element to which the tour step popover attaches when open",
        "name": "children",
        "required": false,
        "type": {
          "name": "string | number | boolean | {} | ReactElement<any, string | ((props: any) => ReactElement<any, string | ... | (new (props: any) => Component<any, any, any>)> | null) | (new (props: any) => Component<...>)> | ... 4 more ... | undefined"
        }
      },
      "content": {
        "defaultValue": null,
        "description": "Contents of the tour step popover",
        "name": "content",
        "required": true,
        "type": {
          "name": "ReactNode"
        }
      },
      "isStepOpen": {
        "defaultValue": {
          value: false
        },
        "description": "Step will display if set to `true`",
        "name": "isStepOpen",
        "required": false,
        "type": {
          "name": "boolean | undefined"
        }
      },
      "minWidth": {
        "defaultValue": {
          value: true
        },
        "description": "Sets the min-width of the tour popover,\nset to `true` to use the default size,\nset to `false` to not restrict the width,\nset to a number for a custom width in px,\nset to a string for a custom width in custom measurement.",
        "name": "minWidth",
        "required": false,
        "type": {
          "name": "string | number | boolean | undefined"
        }
      },
      "onFinish": {
        "defaultValue": null,
        "description": "Function to call for 'Skip tour' and 'End tour' actions",
        "name": "onFinish",
        "required": true,
        "type": {
          "name": "NoArgCallback<void>"
        }
      },
      "step": {
        "defaultValue": {
          value: 1
        },
        "description": "The number of the step within the parent tour. 1-based indexing.",
        "name": "step",
        "required": false,
        "type": {
          "name": "number"
        }
      },
      "stepsTotal": {
        "defaultValue": null,
        "description": "The total number of steps in the tour",
        "name": "stepsTotal",
        "required": true,
        "type": {
          "name": "number"
        }
      },
      "style": {
        "defaultValue": null,
        "description": "Optional, standard DOM `style` attribute. Passed to the EuiPopover panel.",
        "name": "style",
        "required": false,
        "type": {
          "name": "CSSProperties | undefined"
        }
      },
      "subtitle": {
        "defaultValue": null,
        "description": "Smaller title text that appears atop each step in the tour",
        "name": "subtitle",
        "required": true,
        "type": {
          "name": "string"
        }
      },
      "title": {
        "defaultValue": null,
        "description": "Larger title text specific to this step",
        "name": "title",
        "required": true,
        "type": {
          "name": "string"
        }
      },
      "decoration": {
        "defaultValue": {
          value: "beacon"
        },
        "description": "Extra visual indication of step location",
        "name": "decoration",
        "required": false,
        "type": {
          "name": "\"none\" | \"beacon\" | undefined"
        }
      },
      "footerAction": {
        "defaultValue": null,
        "description": "Element to replace the 'Skip tour' link in the footer",
        "name": "footerAction",
        "required": false,
        "type": {
          "name": "ReactElement<any, string | ((props: any) => ReactElement<any, string | ... | (new (props: any) => Component<any, any, any>)> | null) | (new (props: any) => Component<any, any, any>)> | undefined"
        }
      },
      "className": {
        "defaultValue": null,
        "description": "",
        "name": "className",
        "required": false,
        "type": {
          "name": "string | undefined"
        }
      },
      "aria-label": {
        "defaultValue": null,
        "description": "",
        "name": "aria-label",
        "required": false,
        "type": {
          "name": "string | undefined"
        }
      },
      "data-test-subj": {
        "defaultValue": null,
        "description": "",
        "name": "data-test-subj",
        "required": false,
        "type": {
          "name": "string | undefined"
        }
      },
      "display": {
        "defaultValue": null,
        "description": "CSS display type for both the popover and anchor",
        "name": "display",
        "required": false,
        "type": {
          "name": "\"block\" | \"inlineBlock\" | undefined"
        }
      },
      "anchorClassName": {
        "defaultValue": null,
        "description": "",
        "name": "anchorClassName",
        "required": false,
        "type": {
          "name": "string | undefined"
        }
      },
      "offset": {
        "defaultValue": null,
        "description": "Distance away from the anchor that the popover will render.",
        "name": "offset",
        "required": false,
        "type": {
          "name": "number | undefined"
        }
      },
      "buttonRef": {
        "defaultValue": null,
        "description": "",
        "name": "buttonRef",
        "required": false,
        "type": {
          "name": "((instance: HTMLDivElement | null) => void) | undefined"
        }
      },
      "initialFocus": {
        "defaultValue": null,
        "description": "specifies what element should initially have focus; Can be a DOM\nnode, or a selector string (which will be passed to\ndocument.querySelector() to find the DOM node), or a function that\nreturns a DOM node.",
        "name": "initialFocus",
        "required": false,
        "type": {
          "name": "string | HTMLElement | (() => HTMLElement) | undefined"
        }
      },
      "anchorPosition": {
        "defaultValue": {
          value: "leftUp"
        },
        "description": "",
        "name": "anchorPosition",
        "required": false,
        "type": {
          "name": "\"upCenter\" | \"upLeft\" | \"upRight\" | \"downCenter\" | \"downLeft\" | \"downRight\" | \"leftCenter\" | \"leftUp\" | \"leftDown\" | \"rightCenter\" | \"rightUp\" | \"rightDown\" | undefined"
        }
      },
      "attachToAnchor": {
        "defaultValue": null,
        "description": "Style and position alteration for arrow-less, left-aligned\nattachment. Intended for use with inputs as anchors, \u00E0 la\nEuiColorPicker",
        "name": "attachToAnchor",
        "required": false,
        "type": {
          "name": "boolean | undefined"
        }
      },
      "container": {
        "defaultValue": null,
        "description": "",
        "name": "container",
        "required": false,
        "type": {
          "name": "HTMLElement | undefined"
        }
      },
      "hasArrow": {
        "defaultValue": null,
        "description": "",
        "name": "hasArrow",
        "required": false,
        "type": {
          "name": "boolean | undefined"
        }
      },
      "insert": {
        "defaultValue": null,
        "description": "Passed directly to EuiPortal for DOM positioning. Both properties are\nrequired if prop is specified *",
        "name": "insert",
        "required": false,
        "type": {
          "name": "{ sibling: HTMLElement; position: \"before\" | \"after\"; } | undefined"
        }
      },
      "isOpen": {
        "defaultValue": null,
        "description": "",
        "name": "isOpen",
        "required": false,
        "type": {
          "name": "boolean | undefined"
        }
      },
      "ownFocus": {
        "defaultValue": null,
        "description": "",
        "name": "ownFocus",
        "required": false,
        "type": {
          "name": "boolean | undefined"
        }
      },
      "panelClassName": {
        "defaultValue": null,
        "description": "",
        "name": "panelClassName",
        "required": false,
        "type": {
          "name": "string | undefined"
        }
      },
      "panelPaddingSize": {
        "defaultValue": null,
        "description": "",
        "name": "panelPaddingSize",
        "required": false,
        "type": {
          "name": "\"none\" | \"s\" | \"m\" | \"l\" | undefined"
        }
      },
      "panelRef": {
        "defaultValue": null,
        "description": "",
        "name": "panelRef",
        "required": false,
        "type": {
          "name": "((instance: HTMLElement | null) => void) | undefined"
        }
      },
      "panelStyle": {
        "defaultValue": null,
        "description": "Optional, standard DOM `style` attribute. Passed to the EuiPanel.",
        "name": "panelStyle",
        "required": false,
        "type": {
          "name": "CSSProperties | undefined"
        }
      },
      "popoverRef": {
        "defaultValue": null,
        "description": "",
        "name": "popoverRef",
        "required": false,
        "type": {
          "name": "((instance: HTMLDivElement | null) => void) | RefObject<HTMLDivElement> | null | undefined"
        }
      },
      "repositionOnScroll": {
        "defaultValue": null,
        "description": "When `true`, the popover's position is re-calculated when the user\nscrolls, this supports having fixed-position popover anchors.",
        "name": "repositionOnScroll",
        "required": false,
        "type": {
          "name": "boolean | undefined"
        }
      },
      "withTitle": {
        "defaultValue": null,
        "description": "",
        "name": "withTitle",
        "required": false,
        "type": {
          "name": "boolean | undefined"
        }
      },
      "zIndex": {
        "defaultValue": null,
        "description": "By default, popover content inherits the z-index of the anchor\ncomponent; pass zIndex to override",
        "name": "zIndex",
        "required": false,
        "type": {
          "name": "number | undefined"
        }
      },
      "onTrapDeactivation": {
        "defaultValue": null,
        "description": "Function callback for when the focus trap is deactivated",
        "name": "onTrapDeactivation",
        "required": false,
        "type": {
          "name": "((node: HTMLElement) => void) | undefined"
        }
      },
      "buffer": {
        "defaultValue": null,
        "description": "Minimum distance between the popover and the bounding container.\nDefault is 16",
        "name": "buffer",
        "required": false,
        "type": {
          "name": "number | undefined"
        }
      },
      "arrowChildren": {
        "defaultValue": null,
        "description": "Element to pass as the child element of the arrow. Use case is typically limited to an accompanying `EuiBeacon`",
        "name": "arrowChildren",
        "required": false,
        "type": {
          "name": "ReactNode"
        }
      },
      "button": {
        "defaultValue": null,
        "description": "",
        "name": "button",
        "required": false,
        "type": {
          "name": "string | number | boolean | {} | ReactElement<any, string | ((props: any) => ReactElement<any, string | ... | (new (props: any) => Component<any, any, any>)> | null) | (new (props: any) => Component<...>)> | ReactNodeArray | ReactPortal | undefined"
        }
      },
      "closePopover": {
        "defaultValue": {
          value: "() => {}"
        },
        "description": "",
        "name": "closePopover",
        "required": false,
        "type": {
          "name": "NoArgCallback<void> | undefined"
        }
      }
    }
  }; // @ts-ignore

  if (typeof STORYBOOK_REACT_CLASSES !== "undefined") // @ts-ignore
    STORYBOOK_REACT_CLASSES["src/components/tour/tour_step.tsx#EuiTourStep"] = {
      docgenInfo: EuiTourStep.__docgenInfo,
      name: "EuiTourStep",
      path: "src/components/tour/tour_step.tsx#EuiTourStep"
    };
} catch (__react_docgen_typescript_loader_error) {}